import * as React from "react";
import { DatePicker } from "../../../../../../../react-core/Datepicker/DatePicker";
import { InvalidateDate } from "../../../../../../../react-core/Datepicker/Resources/ContentContent";
import { NumberTextBoxEditor } from "../../../../../../../react-core/Html/NumberTextBoxEditor";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { addDays, addYears, stripToDay } from "../../../../../../../react-core/utils/date";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { Objects } from "../../../../../../../react-core/Utils/Objects";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { Arrangements } from "../../../../../../../Wrap/Models/Domains/Drawdown/Arrangements";
import { DrawdownAnnualIncome } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownAnnualIncome";
import { PensionProtectionDetails } from "../../../../../../../Wrap/Models/Models/Customer/PensionProtection/PensionProtectionDetails";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { MoneyHelper } from "../../../../../../Shared/Common/MoneyHelper";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { datePickerOptions, processTypeEnum } from "../../Resource/Contents";
import { CappedFlexiTable } from "./CappedFlexiTable";
import { getAssetsToSellTotalForIncomeFromIncome } from "./Helper/IncomePaymentHelper";
import { IncomeArrangementType, incomePaymentDetailsLabels, incomePaymentDetailsMessages } from "./Resource/Content";

export interface CappedFlexiSectionProps extends IProcessComponentProps {
    processType?: processTypeEnum;
    data: Arrangements;
    income: DrawdownAnnualIncome;
    drawdownType: DrawdownType;
    onIncomeChange: (v) => void;
    onChange: (v) => void;
    modelData?: DrawdownModel;
    pensionProtection?: PensionProtectionDetails;
}

interface ICappedFlexiSectionState {}

export class CappedFlexiSection extends ProcessComponent<CappedFlexiSectionProps, ICappedFlexiSectionState> {
    constructor(props: any) {
        super(props);
    }

    private handleChange(changes) {
        this.props.onIncomeChange(changes);
    }

    public isValid(): boolean {
        let pass = super.isValid();
        if (pass && this.isValidTotal()) {
            pass = false;
            this.props.showErrorMsgPopUp([incomePaymentDetailsMessages.grossIncomeNotSumOfArrangementsIncome]);
        }
        return pass;
    }

    isValidTotal() {
        return this.cappedFlexiTotals() !== getAssetsToSellTotalForIncomeFromIncome(this.props.income);
    }

    cappedFlexiTotals(): number {
        const cappedModels = this.props.data?.CappedModels;
        const cappedTotals = cappedModels
            ? cappedModels.reduce((sum, model) => {
                  return sum + (model.IncomePerPaymentForCurrentPensionYear || 0);
              }, 0)
            : 0;
        return MoneyHelper.roundNumberToDecimalPlaces(cappedTotals + this.flexiTotals(), 2);
    }

    flexiTotals() {
        const flexiModels = this.props.data?.FlexiModels;

        return flexiModels
            ? flexiModels.reduce((sum, model) => {
                  return sum + (model.IncomePerPayment || 0);
              }, 0)
            : 0;
    }

    yearFromNow = () => {
        const result = stripToDay(addYears(new Date(Date.now()), 1));
        if (result.getDate() > 28) {
            result.setDate(28);
        }
        return result;
    };

    updateValue = (index: number, key: string, value: number, type: IncomeArrangementType) => {
        const { data, onChange } = this.props;
        const capped = type === IncomeArrangementType.Capped;
        const list = capped ? data?.CappedModels : data?.FlexiModels;
        if (list && list.length > index) {
            const updatedList = Objects.cloneObject(list);
            updatedList[index][key] = value;
            const change = capped ? { CappedModels: updatedList } : { FlexiModels: updatedList };
            onChange(change);
        }
    };

    public render(): JSX.Element {
        const { data, processType, modelData, pensionProtection } = this.props;
        return (
            <>
                {!isNullOrEmpty(data?.CappedModels) && (
                    <CappedFlexiTable
                        data={data?.CappedModels}
                        type={IncomeArrangementType.Capped}
                        onChange={(index, key, value) =>
                            this.updateValue(index, key, value, IncomeArrangementType.Capped)
                        }
                        drawdown={modelData}
                        processType={processType}
                        pensionProtection={pensionProtection}
                    />
                )}
                {!isNullOrEmpty(data?.FlexiModels) && (
                    <CappedFlexiTable
                        data={data?.FlexiModels}
                        type={IncomeArrangementType.Flexi}
                        onChange={(index, key, value) =>
                            this.updateValue(index, key, value, IncomeArrangementType.Flexi)
                        }
                        drawdown={modelData}
                        processType={processType}
                        pensionProtection={pensionProtection}
                    />
                )}
                {this.props.drawdownType == DrawdownType.Flexible && (
                    <>
                        <div className="row">
                            <div className="col-12">
                                {this.props.income?.FlexiIncomeReviewDate &&
                                    JsonDateParser.toDate(this.props.income?.FlexiIncomeReviewDate) >
                                        JsonDateParser.toDate(addYears(new Date(), 3)) && (
                                        <Alert
                                            alertType={AlertType.Warning}
                                            message={incomePaymentDetailsMessages.confirmIncomeReviewDate}
                                        />
                                    )}
                                <DatePicker
                                    formGroupCssWrapper={"control-size-430"}
                                    selected={
                                        this.props.income?.FlexiIncomeReviewDate &&
                                        this.props.income?.FlexiIncomeReviewDate.toString() != InvalidateDate
                                            ? JsonDateParser.toDateNoOffset(this.props.income?.FlexiIncomeReviewDate)
                                            : this.yearFromNow()
                                    }
                                    label={incomePaymentDetailsLabels.nextFlexiReviewDate}
                                    {...datePickerOptions}
                                    alwaysValidateOnUpdate={true}
                                    atLeast={{
                                        value: stripToDay(addDays(new Date(Date.now()), 20)),
                                        errorMessage: incomePaymentDetailsMessages.nextFlexiReviewDateUnder,
                                    }}
                                    filterDate={{
                                        errorMessage: incomePaymentDetailsMessages.dateOver28,
                                        validator: (v) => v.getDate() <= 28,
                                    }}
                                    required={incomePaymentDetailsMessages.nextFlexiReviewDateRequired}
                                    onChange={(v) =>
                                        this.handleChange({
                                            FlexiIncomeReviewDate: JsonDateParser.toJsonDateNoOffset(v),
                                        })
                                    }
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <NumberTextBoxEditor
                                    cssWrapper="control-size-430"
                                    value={
                                        this.props.income?.FrequencyFlexiIncomeReview
                                            ? this.props.income?.FrequencyFlexiIncomeReview
                                            : 12
                                    }
                                    moreThan={{
                                        errorMessage:
                                            incomePaymentDetailsMessages.frequencyOfFlexiIncomeReviewUnderOver,
                                        value: 0,
                                    }}
                                    atMost={{
                                        errorMessage:
                                            incomePaymentDetailsMessages.frequencyOfFlexiIncomeReviewUnderOver,
                                        value: 60,
                                    }}
                                    required={
                                        this.props.drawdownType == DrawdownType.Flexible
                                            ? "The frequency if reviews must be entered"
                                            : null
                                    }
                                    label={incomePaymentDetailsLabels.frequencyOfFlexiIncomeReview}
                                    onChange={(v) =>
                                        this.handleChange({
                                            FrequencyFlexiIncomeReview: v,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    }
}
